import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Dashboard as DashboardIcon,
  RateReview as ReviewIcon,
  FactCheck as IndexChannelIcon,
  Timeline as TimelineIcon,
  Chat as ChannelsIcon,
  LibraryBooks as LibraryIcon,
  ListAlt as EntriesIcon,
  People as PeopleIcon,
  Forum as ChatPreviewIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useAuth } from '@/hooks/useAuth';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation(['common', 'navigation']);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  // Navigation tabs with access control
  const allTabs = [
    { label: t('navigation:dashboard'), path: '/', slug: 'dashboard', icon: <DashboardIcon /> },
    { label: t('navigation:review'), path: '/review', slug: 'review', icon: <ReviewIcon /> },
    { label: t('navigation:indexChannel'), path: '/index-channel', slug: 'index-channel', icon: <IndexChannelIcon /> },
    { label: t('navigation:timeline'), path: '/timeline', slug: 'timeline', icon: <TimelineIcon /> },
    { label: t('navigation:channels'), path: '/channels', slug: 'channels', icon: <ChannelsIcon /> },
    { label: t('navigation:libraries'), path: '/libraries', slug: 'libraries', icon: <LibraryIcon /> },
    { label: t('navigation:entries'), path: '/entries', slug: 'entries', icon: <EntriesIcon /> },
    { label: t('navigation:chatPreview'), path: '/chat-preview', slug: 'chat-preview', icon: <ChatPreviewIcon /> },
    { label: t('navigation:users'), path: '/users', slug: 'users', icon: <PeopleIcon />, adminOnly: true },
    { label: t('navigation:settings'), path: '/settings', slug: 'settings', icon: <SettingsIcon />, adminOnly: true },
    { label: t('navigation:sync'), path: '/sync', slug: 'sync', icon: <SyncIcon />, adminOnly: true },
  ];

  // Tier = manuscript_role, not the ilm dashboard `role` column (decision
  // 2026-08-18); same predicate as useAuth().isAdmin() and the backend's
  // require_admin.
  const tabs = allTabs.filter((tab) => {
    if ((tab as any).adminOnly) return user?.manuscript_role === 'admin';
    if (user?.manuscript_role === 'admin') return true;
    if (!user?.allowed_pages) return true; // null = all pages
    return user.allowed_pages.includes(tab.slug);
  });

  // Find current tab index
  const currentTabIndex = tabs.findIndex((tab) => {
    if (tab.path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(tab.path);
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    navigate(tabs[newValue].path);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" elevation={1}>
        <Toolbar sx={{ minHeight: '64px' }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 0, mr: 4 }}>
            {t('app.name')}
          </Typography>

          <Tabs
            value={currentTabIndex !== -1 ? currentTabIndex : false}
            onChange={handleTabChange}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              flexGrow: 1,
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-selected': {
                  color: 'white',
                },
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.path} label={tab.label} icon={tab.icon} iconPosition="start" />
            ))}
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LanguageSwitcher />
            <Typography variant="body2" sx={{ mx: 1.5 }}>
              {user?.display_name || user?.username}
            </Typography>
            <IconButton
              size="large"
              aria-label="account menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircleIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {t('labels.role')}: {user?.role}
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleLogout}>{t('actions.logout')}</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', py: 3, pt: '64px' }}>
        <Container maxWidth="xl">
          <Outlet />
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
        }}
      >
        <Container maxWidth="xl">
          <Typography variant="body2" color="text.secondary" align="center">
            {t('app.name')} - {t('app.tagline')}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default AppLayout;
